import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getTales, getPublicTales } from '../../actions/tales';
import Tale from './Tale/Tale';
import { Grid, CircularProgress, Typography, Divider, Container, TextField, Box, Button, Paper } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import styled from '@emotion/styled';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../Auth/LoginModal';

const PageTitle = styled(Typography)(({ groupColor }) => ({
  marginBottom: '24px',
  fontWeight: 600,
  fontFamily: '"Literata", Georgia, serif',
  color: groupColor || '#4A3428',
  display: 'inline-block'
}));

const Tales = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [filteredTales, setFilteredTales] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [showLoginModal, setShowLoginModal] = useState(false);

    const groupName = decodeURIComponent(new URLSearchParams(location.search).get('group') || '');
    const tales = useSelector((state) => state.tales.tales || state.tales);
    const groups = useSelector((state) => state.groups);
    const { user, isAuthenticated, token } = useSelector((state) => state.auth);
    const { isAuthenticated: authIsAuthenticated } = useAuth();

    useEffect(() => {
        const fetchTales = async () => {
            setLoading(true);
            try {
                if (authIsAuthenticated) {
                    await dispatch(getTales());
                } else {
                    await dispatch(getPublicTales());
                }
            } catch (error) {
                console.error('Error fetching tales:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchTales();
    }, [dispatch, authIsAuthenticated]);

    const handleLoginModalClose = () => {
        setShowLoginModal(false);
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        window.location.reload();
    };

    useEffect(() => {
        if (tales) {
            let filtered = tales;
            
            if (groupName) {
                filtered = filtered.filter(tale => tale.group === groupName);
            }
            
            if (searchQuery) {
                filtered = filtered.filter(tale => 
                    tale.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    tale.story?.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            
            setFilteredTales(filtered);
        }
    }, [tales, groupName, searchQuery]);


    const currentGroup = groups.find(group => group.name === groupName);

    if (loading) {
        return (
            <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!tales?.length) {
        return (
            <Container maxWidth={false} sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Typography variant="body1" color="textSecondary">
                    No tales have been created yet.
                </Typography>
            </Container>
        );
    }

    return (
        <Container 
            maxWidth={false} 
            sx={{ 
                padding: { xs: '16px', sm: '24px', md: '32px' },
                minHeight: 'calc(100vh - 64px)', // Subtract navbar height
                backgroundColor: '#F0EFED'
            }}
        >                                                                                                       
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                marginBottom: '16px'
            }}>
                <PageTitle 
                    variant="h5"
                    groupColor={currentGroup?.color}
                >
                    {currentGroup 
                        ? `Tales from ${groupName}`
                        : 'All Tales'
                    }
                </PageTitle>
                
                <TextField
                    variant="outlined"
                    placeholder="Search tales..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ 
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        maxWidth: '300px',
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                            },
                        },
                    }}
                    size="small"
                />
            </div>
            <Divider sx={{ marginBottom: '24px' }} />
            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                }}
            >
                <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                    Real rejection stories from careers, school, startups, and life
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Rejection Tales is a public collection of rejection experiences that helps you
                    learn from real people and recover faster. Browse highlights for interview
                    rejection, admissions rejection, startup rejection, and personal setbacks.
                    {authIsAuthenticated
                        ? ' You are logged in. Open any story, comment, and share your own experience.'
                        : ' Click any story to read it in full. Sign in to leave comments and share your own rejection tale.'}
                </Typography>
                {!authIsAuthenticated && (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            component={Link}
                            to="/auth"
                            sx={{
                                fontSize: '0.9rem',
                                color: 'primary.main',
                                fontWeight: 600,
                                textDecoration: 'none'
                            }}
                        >
                            Sign in to share your story
                        </Typography>
                    </Box>
                )}
            </Box>
            
            {/* Luna AI teaser for logged-out users */}
            {!authIsAuthenticated && (
                <Paper
                    elevation={0}
                    sx={{
                        mb: 3,
                        p: 2.5,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: '#DDD6CF',
                        background: 'linear-gradient(135deg, #F7F6F4 0%, #EDE8E3 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Meet Luna, your AI resilience coach
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Feeling stuck after a rejection? Luna listens and helps you move forward.
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        component={Link}
                        to="/auth"
                        variant="contained"
                        size="small"
                        sx={{ borderRadius: '20px', textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                        Try Luna free
                    </Button>
                </Paper>
            )}

            {filteredTales.length === 0 && groupName ? (
                <Typography variant="body1" color="textSecondary">
                    No tales found in this group yet.
                </Typography>
            ) : (
                <Grid 
                    container 
                    spacing={3}
                    sx={{
                        margin: '0 auto',
                        width: '100%'
                    }}
                >
                    {filteredTales.map((tale) => (
                        <Grid 
                            key={tale._id} 
                            item 
                            xs={12} // Full width on mobile
                            sx={{
                                display: 'flex',
                                alignItems: 'stretch'
                            }}
                        >
                            <div 
                                style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    width: '100%',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                }}
                            >
                                <Tale 
                                    tale={tale} 
                                    currentUser={user}
                                />
                            </div>
                        </Grid>
                    ))}
                </Grid>
            )}
            <LoginModal
                open={showLoginModal}
                onClose={handleLoginModalClose}
                onLoginSuccess={handleLoginSuccess}
            />
        </Container>
    );
};

export default Tales;